import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BackArrow from '../../assets/images/Frame_18177.png';
import WhatsappIconIMG from '../../assets/images/whatsapp-icon.png';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { Download } from 'lucide-react';
import InactivityTimer from '../../components/InactivityTimer';

const Billing = () => {
    const [billingData, setBillingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [whatsappNo, setWhatsappNo] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tempWhatsappNo, setTempWhatsappNo] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingInvoiceUrl, setViewingInvoiceUrl] = useState('');
    const [printedInvoices, setPrintedInvoices] = useState(new Set());
    const [printingInvoice, setPrintingInvoice] = useState(null);
    const [patientFullName, setPatientFullName] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const patientNameFromNav = location.state?.patientName || '';
    const handleLogout = () => {
        localStorage.removeItem('kiosk_token');
        localStorage.removeItem('patient_profile');
        navigate("/", { replace: true });
    };

    const token = useSelector((state) => state.auth.kiosk_token);

    // Validate token exists and redirect if not
    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
        }
    }, [token, navigate]);

    useEffect(() => {
        const fetchBillingData = async () => {
            if (!token) return; // Skip if no token

            try {
                setLoading(true);
                const decodedToken = jwtDecode(token);
                const response = await axios.get(
                    `${process.env.REACT_APP_KIOSK_OPS_URL}/invoice/details?mrno=${decodedToken.mrno}`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                        },
                    }
                );
                console.log("Billing Data Response:", response.data);

                // Extract invoice details from response
                if (response.data && response.data.status === 'success' && Array.isArray(response.data.invoiceDetails)) {
                    setBillingData(response.data.invoiceDetails);
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching billing data:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        const fetchPatientData = async () => {
            try {
                const decodedToken = jwtDecode(token);
                const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(decodedToken.mrno)}`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                    },
                });

                if (response.data && response.data.data && response.data.data.length > 0) {
                    const patientData = response.data.data[0];
                    setWhatsappNo(patientData.contactNo);

                    // Format full name: prefix + firstname + middlename + lastname
                    const nameParts = [
                        patientData.prefix,
                        patientData.firstName,
                        patientData.middleName,
                        patientData.lastName
                    ].filter(part => part && part.trim() !== ''); // Filter out empty/null values

                    const fullName = nameParts.join(' ');
                    setPatientFullName(fullName);
                }
            } catch (err) {
                console.error("Error fetching patient data:", err);
            }
        };

        fetchBillingData();

        // Set patient name from navigation state
        if (patientNameFromNav) {
            setPatientFullName(patientNameFromNav);
        }
    }, [token, patientNameFromNav]); // Use token instead of decodedToken.mrno

    // Decode token for use in render (after early return check)
    const decodedToken = jwtDecode(token);
    const userId = decodedToken?.id;

    // Check print statuses after billing data is loaded
    useEffect(() => {
        const checkPrintStatuses = async () => {
            if (!userId || billingData.length === 0) return;

            // Get unique invoice IDs and filter out undefined/null
            const invoiceIds = [...new Set(billingData
                .map(invoice => invoice.invoiceId)
                .filter(id => id !== undefined && id !== null))];

            if (invoiceIds.length === 0) return; // Skip if no valid IDs

            const printedSet = new Set();
            for (const invoiceId of invoiceIds) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/check-print-status`,
                        {
                            params: {
                                reportType: 'billing',
                                reportId: String(invoiceId),
                                userId: userId
                            }
                        }
                    );
                    if (response.data.alreadyPrinted) {
                        printedSet.add(invoiceId);
                    }
                } catch (err) {
                    console.error('Error checking print status:', err);
                }
            }
            setPrintedInvoices(printedSet);
        };
        checkPrintStatuses();
    }, [billingData, userId]);

    const handleDownloadInvoice = async (invoiceId) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/invoice/download?printType=INVOICE&invoiceId=${invoiceId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                    },
                    responseType: 'blob'
                }
            );

            // Create a blob URL and open in new tab
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');

            // Clean up the URL after a delay
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (err) {
            console.error("Error opening invoice:", err);
            alert('Failed to open invoice. Please try again.');
        }
    };

    const handleWhatsappClick = (invoice) => {
        setSelectedInvoice(invoice);
        setTempWhatsappNo(whatsappNo);
        setIsModalOpen(true);
    };

    // const handleViewInvoice = (invoiceId) => {
    //     const url = `${process.env.REACT_APP_KIOSK_OPS_URL}/invoice/download?printType=INVOICE&invoiceId=${invoiceId}`;
    //     setViewingInvoiceUrl(url);
    //     setViewModalOpen(true);
    //     setPrintIntent(false);
    // };

    const handlePrintInvoice = async (invoice) => {
        const invoiceId = invoice.invoiceId;
        if (printedInvoices.has(invoiceId)) {
            alert('You can only print this invoice once from the kiosk. If you need another copy, please contact reception.');
            return;
        }
        try {
            setPrintingInvoice(invoiceId);

            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/print`, {
                reportType: 'billing',
                reportId: String(invoiceId),
                userId,
            });
            if (response.data.alreadyPrinted) {
                alert(response.data.message);
                setPrintedInvoices(prev => new Set([...prev, invoiceId]));
                setPrintingInvoice(null);
                return;
            }
            setPrintedInvoices(prev => new Set([...prev, invoiceId]));

            // Fetch PDF as blob to avoid CORS issues
            const pdfResponse = await fetch(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/invoice/download?printType=INVOICE&invoiceId=${invoiceId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                    },
                }
            );
            const blob = await pdfResponse.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Create hidden iframe with blob URL
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = blobUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                try {
                    iframe.contentWindow.print();
                    // Cleanup
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                        URL.revokeObjectURL(blobUrl);
                        setPrintingInvoice(null);
                    }, 1000);
                } catch (err) {
                    console.error('Error printing:', err);
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(blobUrl);
                    setPrintingInvoice(null);
                }
            };
        } catch (err) {
            console.error('Error tracking invoice print:', err);
            alert('Failed to print invoice. Please try again.');
            setPrintingInvoice(null);
        }
    };

    const handleViewInvoice = async (invoiceId) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/view`, {
                reportType: 'billing',
                reportId: String(invoiceId),
                userId: decodedToken?.id,
            });
        } catch (err) {
            console.error('Error tracking invoice view:', err);
        }

        try {
            // Fetch the invoice PDF as blob with authorization header
            const response = await axios.get(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/invoice/download?printType=INVOICE&invoiceId=${invoiceId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                    },
                    responseType: 'blob'
                }
            );

            // Create blob URL and set it for iframe
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            setViewingInvoiceUrl(blobUrl);
            setViewModalOpen(true);
        } catch (err) {
            console.error('Error fetching invoice:', err);
            alert('Failed to load invoice. Please try again.');
        }
    };

    const sendWhatsappInvoice = async (phoneNumber, invoice) => {
        console.log('Sending invoice:', invoice);
        try {
            setSendingWhatsapp(true);

            // Get the auth token
            const authToken = localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1');

            // Call backend proxy endpoint instead of WhatsApp API directly
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/send-invoice`, {
                phoneNumber: phoneNumber,
                invoiceId: invoice.invoiceId,
                invoiceNo: invoice.invoiceNo,
                createdDate: invoice.createdDate,
                patientName: patientFullName || decodedToken.name || "Patient",
                authToken: authToken
            });

            console.log('WhatsApp sent successfully:', response.data);
            alert('Invoice sent successfully via WhatsApp!');
            setSendingWhatsapp(false);
            setIsModalOpen(false);
        } catch (err) {
            console.error('Error sending WhatsApp:', err);
            console.error('Error response:', err.response?.data);
            console.error('Error status:', err.response?.status);
            console.error('Error message:', err.message);

            const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
            alert(`Failed to send WhatsApp message: ${errorMessage}\n\nPlease check the console for details.`);
            setSendingWhatsapp(false);
        }
    };

    const handleModalSubmit = () => {
        if (tempWhatsappNo && tempWhatsappNo.length === 10) {
            setWhatsappNo(tempWhatsappNo);
            sendWhatsappInvoice(tempWhatsappNo, selectedInvoice);
        } else {
            alert('Please enter a valid 10-digit WhatsApp number');
        }
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dateString; // Already in good format like "Nov 11, 2025"
    };

    // Helper function to get status badge color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'settled':
                return 'bg-green-100 text-green-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen overflow-x-hidden">
                <div className="flex flex-wrap items-center gap-3 px-4 sm:px-8 lg:px-14 pt-4 sm:pt-6">
                    <Link
                        to="/dashboard"
                        className="flex w-auto min-w-[7rem] px-4 py-2 sm:py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                        style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}
                    >
                        <img src={BackArrow} alt="Back" className="mr-2 w-5 h-5 sm:w-6 sm:h-6" />
                        Back
                    </Link>
                    <span className="text-xl sm:text-2xl lg:text-[28px] font-semibold" style={{ fontFamily: 'Roboto', fontWeight: '600', lineHeight: '1.2' }}>
                        Billing
                    </span>
                </div>

                <hr className="mt-4 sm:mt-6 mx-4 sm:mx-8 lg:mx-14 border-t border-gray-300" />

            {loading ? (
                <div className="flex items-center justify-center min-h-[50vh] sm:min-h-[60vh] px-4">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-[#0FA968] mx-auto mb-4"></div>
                        <p className="text-base sm:text-xl text-gray-600">Loading Billing Information...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="flex items-center justify-center min-h-[50vh] sm:min-h-[60vh] px-4">
                    <div className="text-center">
                        <h1 className="text-2xl sm:text-4xl font-bold text-red-500 mb-4">Error</h1>
                        <p className="text-base sm:text-xl text-gray-600">{error}</p>
                    </div>
                </div>
            ) : (
                <>
                    {billingData.length > 0 && (
                        <div className="mx-4 sm:mx-8 lg:mx-14 mt-4 sm:mt-6 bg-gradient-to-r from-[#0FA968] to-[#138F5E] text-white p-4 sm:p-6 rounded-xl shadow-lg max-w-[1600px]">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                <div>
                                    <p className="text-xs sm:text-sm opacity-90">Patient Name</p>
                                    <p className="text-base sm:text-lg font-semibold break-words">{patientFullName || decodedToken.name || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-xs sm:text-sm opacity-90">MRN</p>
                                    <p className="text-base sm:text-lg font-semibold break-words">{decodedToken.mrno}</p>
                                </div>
                                <div>
                                    <p className="text-xs sm:text-sm opacity-90">Total Invoices</p>
                                    <p className="text-base sm:text-lg font-semibold">{billingData.length}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mx-4 sm:mx-8 lg:mx-14 my-4 sm:my-6 max-w-[1600px]">
                        {billingData.length > 0 ? (
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[900px]">
                                        <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                            <tr>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Invoice No</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Invoice Type</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Visit No</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Bill Date</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-right text-xs sm:text-sm font-semibold uppercase">Amount (₹)</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Status</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-semibold uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {billingData.map((invoice, index) => (
                                                <tr key={invoice.invoiceId || index} className={`hover:bg-blue-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <p className="font-semibold text-gray-800 text-sm sm:text-base break-words">{invoice.invoiceNo}</p>
                                                        <p className="text-xs text-gray-500">ID: {invoice.invoiceId}</p>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <span className="text-gray-700 font-medium text-sm sm:text-base">{invoice.invoiceType}</span>
                                                        <p className="text-xs text-gray-500">{invoice.visitType}</p>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <span className="text-gray-700 font-medium text-sm sm:text-base">{invoice.visitNo || 'N/A'}</span>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <span className="text-gray-700 font-medium text-sm sm:text-base whitespace-nowrap">{formatDate(invoice.createdDate)}</span>
                                                        <p className="text-xs text-gray-500">Approved: {formatDate(invoice.approvedDate)}</p>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-right">
                                                        <p className="font-bold text-gray-800 text-sm sm:text-base whitespace-nowrap">₹{invoice.patientAmount?.toFixed(2)}</p>
                                                        <p className="text-xs text-gray-500 whitespace-nowrap">Total: ₹{invoice.totalAmount?.toFixed(2)}</p>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <span className={`inline-block px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(invoice.invoiceStatus)}`}>
                                                            {invoice.invoiceStatus}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <div className="flex flex-wrap justify-center items-center gap-2">
                                                            {/* <button
                                                                onClick={() => handleDownloadInvoice(invoice.invoiceId)}
                                                                className='bg-[#0FA968] text-white rounded-lg px-4 py-2 font-semibold hover:bg-[#138F5E] transition duration-200 shadow text-sm flex items-center gap-2'>
                                                                <Download className="w-4 h-4" />
                                                                View Invoice
                                                            </button> */}
                                                            {/* <button
                                                                onClick={() => handleViewInvoice(invoice.invoiceId)}
                                                                className='bg-[#0FA968] text-white rounded-lg px-4 py-2 font-semibold hover:bg-[#138F5E] transition duration-200 shadow text-sm flex items-center gap-2'>
                                                                <Download className="w-4 h-4" />
                                                                View Invoice
                                                            </button> */}
                                                            <button
                                                                onClick={() => handlePrintInvoice(invoice)}
                                                                disabled={printedInvoices.has(invoice.invoiceId) || printingInvoice !== null}
                                                                className={`bg-transparent border border-[#0FA968] rounded-lg p-2 transition duration-200 ${printedInvoices.has(invoice.invoiceId) || printingInvoice !== null
                                                                    ? 'opacity-50 cursor-not-allowed bg-gray-200'
                                                                    : 'hover:bg-[#0FA968] hover:text-white'
                                                                    }`}
                                                                title={printedInvoices.has(invoice.invoiceId) ? 'Already printed - Contact reception for more copies' : 'Print invoice'}
                                                            >
                                                                {/* Print Icon SVG */}
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="green">
                                                                    <path d="M6 2h12v4H6V2zm12 6H6c-1.1 0-2 .9-2 2v6h4v4h8v-4h4v-6c0-1.1-.9-2-2-2zm-4 10H10v-2h4v2z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleWhatsappClick(invoice)}
                                                                disabled={sendingWhatsapp}
                                                                className='bg-[#EFFEF7] rounded-lg px-3 py-2 border-2 border-[#0FA968] hover:bg-[#0FA968] hover:bg-opacity-10 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed'>
                                                                <img src={WhatsappIconIMG} alt='Whatsapp' className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center min-h-[30vh] sm:min-h-[40vh] px-4">
                                <div className="text-center">
                                    <h2 className="text-xl sm:text-2xl font-bold text-gray-400 mb-2">No Billing Records Found</h2>
                                    <p className="text-sm sm:text-base text-gray-500">You don't have any invoices yet.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {viewModalOpen && (
                <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-75 report-modal-container px-2 sm:px-4">
                    <div className="relative bg-white rounded-lg shadow-lg w-full max-w-6xl h-[85vh] sm:h-[90vh]">
                        <button
                            onClick={() => {
                                setViewModalOpen(false);
                            }}
                            className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                            aria-label="Close"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <iframe
                            src={viewingInvoiceUrl}
                            title="Billing Invoice"
                            className="w-full h-full rounded-lg report-modal-iframe"
                            frameBorder="0"
                            referrerPolicy="no-referrer"
                        />
                    </div>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 w-full max-w-md my-4">
                        <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
                            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
                                {whatsappNo ? 'Change WhatsApp Number' : 'Enter WhatsApp Number'}
                            </h2>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setTempWhatsappNo('');
                                }}
                                className="text-gray-400 hover:text-gray-600 transition">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {whatsappNo && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-800">
                                    Current Number: <span className="font-semibold">{whatsappNo}</span>
                                </p>
                            </div>
                        )}

                        <div className="mb-6">
                            <label className="block text-gray-700 font-semibold mb-2">
                                WhatsApp Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                maxLength="10"
                                placeholder="Enter 10-digit number"
                                value={tempWhatsappNo}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setTempWhatsappNo(value);
                                }}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0FA968] focus:outline-none text-lg"
                            />
                            {tempWhatsappNo && tempWhatsappNo.length !== 10 && (
                                <p className="text-red-500 text-sm mt-1">Please enter a valid 10-digit number</p>
                            )}
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row gap-3">
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setTempWhatsappNo('');
                                }}
                                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition duration-200">
                                Cancel
                            </button>
                            <button
                                onClick={handleModalSubmit}
                                disabled={sendingWhatsapp || !tempWhatsappNo || tempWhatsappNo.length !== 10}
                                className="flex-1 px-6 py-3 bg-[#0FA968] text-white rounded-lg font-semibold hover:bg-[#138F5E] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                {sendingWhatsapp ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <img src={WhatsappIconIMG} alt='WhatsApp' className="w-5 h-5" />
                                        Send Invoice
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {printingInvoice !== null && (
                <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-75 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-center w-full max-w-sm">
                        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-[#0FA968] border-t-transparent mx-auto mb-4"></div>
                        <p className="text-lg sm:text-xl font-semibold text-gray-800">Preparing to print...</p>
                        <p className="text-xs sm:text-sm text-gray-600 mt-2">Please wait while we process your invoice</p>
                    </div>
                </div>
            )}
            </div>
        </>
    )
}

export default Billing;